import {createContext, ReactNode, useContext, useState} from 'react';
import {Flow, Params} from 'react-chatbotify';
import {MarkdownRendererBlock} from '@rcb-plugins/markdown-renderer';
import {useIntl} from 'react-intl';
import {useFormik} from 'formik';
import {useUserContext} from './UserContext';
import {useButtonSendChatHistoryContext} from './ButtonSendChatHistoryContext';
import {QuestionnaireData} from '../types';
import {useEndOfChat} from '../components/hooks';
import {useAnimationsContext} from './AnimationsContext';
import {FlashyHelper} from '../utils';
import {useGlobalContext} from './GlobalContext';

type ChatContextType = {
  id: string;
  flow: Flow;
}

type FormValues = {
  timeOfYear: string;
  region: string;
  kids: boolean;
  babies: boolean;
  dogs: boolean;
  activities: string[];
  trailsOptions: string;
  mealOptions: string;
}

interface ChatContextProviderProps {
  children: ReactNode;
}

const tuningOffset = 1000;

export const NewUserChatContext = createContext<ChatContextType>({id: '', flow: {}});

export const useNewUserChatContext = () => useContext(NewUserChatContext);

export const NewUserChatContextProvider = ({children}: ChatContextProviderProps) => {
  const intl = useIntl();
  const {user, updateUser} = useUserContext();
  const {api, aiAgent} = useGlobalContext();
  const [error, setError] = useState(false);
  const {enableSendEmailButton, disableSendEmailButton} = useButtonSendChatHistoryContext();
  const formik = useFormik<FormValues>({
    initialValues: {
      timeOfYear: '',
      region: '',
      kids: false,
      babies: false,
      dogs: false,
      activities: [],
      trailsOptions: '',
      mealOptions: '',
    },
    onSubmit: () => {}
  });
  const {
    startAnswersFlowTimer,
    stopAnswersFlowTimer,
    startAssistantFlowTimer,
    clearFlowTimers,
    timeoutMessagesFlow,
  } = useEndOfChat();
  const {
    animations,
    handleUserInputAnimations,
    handleStartThinkingAnimation,
    handleStopThinkingAnimation,
    animationDuration,
  } = useAnimationsContext();

  const saveAnswers = async (values: FormValues) => {
    try {
      await api.saveAnswers(
        user.contactID,
        values.timeOfYear,
        values.region,
        values.kids,
        values.babies,
        values.dogs,
        values.activities,
        values.trailsOptions,
        values.mealOptions,
      );
    } catch (e) {
      console.log(e);
    }
  };

  const updateContactAndUser = async (values: FormValues) => {
    try {
      const [vacationsType] = values.activities;
      const flashyResponse = await api.updateContact(
        user.contactID,
        user.firstName,
        user.lastName,
        user.email,
        user.phone,
        values.babies,
        user.communityMember,
        values.kids,
        FlashyHelper.dogOwnerText(values.dogs, intl),
        vacationsType,
        values.trailsOptions
      );
      const response = await api.updateUser(
        flashyResponse.user.contactID,
        flashyResponse.user.firstName,
        flashyResponse.user.lastName,
        flashyResponse.user.email,
        flashyResponse.user.phone,
        Boolean(flashyResponse.user.babies),
        Boolean(flashyResponse.user.communityMember),
        Boolean(flashyResponse.user.kids12),
        flashyResponse.user.dogsOwners,
        flashyResponse.user.vacationsType,
        flashyResponse.user.travelevel
      );
      updateUser(response.user);
    } catch (e) {
      console.log(e);
    }
  };

  const callAiAgent = async (
    params: Params,
    questionnaireData?: QuestionnaireData
  ) => {
    handleStartThinkingAnimation();
    const timer = setTimeout(async () => {
      await params.injectMessage(intl.formatMessage({
          id: 'flow-msg-assistant-preparing-recommendations',
          defaultMessage: 'Wait a minute! I am preparing recommendations just for you!'
        })
      );
      clearTimeout(timer);
    }, animationDuration + tuningOffset);
    try {
      setError(false);
      await aiAgent.stream(
        params.userInput,
        async (chunkText) => {
          clearTimeout(timer);
          handleStopThinkingAnimation();
          await params.streamMessage(chunkText);
        },
        questionnaireData
      );

      await params.endStreamMessage('BOT');
    } catch (error) {
      setError(true);
      await params.injectMessage('Unable to load model!');
    } finally {
      handleStopThinkingAnimation();
      clearTimeout(timer);
    }
  };

  const getQuestionnaireData = () => ({
    activities: formik.values.activities,
    time_of_year: formik.values.timeOfYear,
    region: formik.values.region,
    kids_12: formik.values.kids,
    babies: formik.values.babies,
    dogs: formik.values.dogs,
    trails_options: formik.values.trailsOptions,
    meal_options: formik.values.mealOptions
  });

  const flow: Flow = {
    start: {
      message: () => intl.formatMessage(
        {id: 'flow-msg-start', defaultMessage: 'Hi {name}'},
        {name: user.firstName || 'Test User'}
      ),
      function: () => disableSendEmailButton(),
      transition: {duration: 1000},
      path: 'startNext',
      chatDisabled: true,
    },
    startNext: {
      message: () => intl.formatMessage({id: 'flow-msg-start-next', defaultMessage: 'I have a few questions'}),
      transition: {duration: 1000},
      path: 'timeQuestion',
      chatDisabled: true,
    },
    timeQuestion: {
      message: () => intl.formatMessage({id: 'flow-msg-time-question', defaultMessage: 'What time of year?'}),
      options: [
        intl.formatMessage({id: 'flow-msg-time-answer-spring', defaultMessage: 'March-May'}),
        intl.formatMessage({id: 'flow-msg-time-answer-summer', defaultMessage: 'June-August'}),
        intl.formatMessage({id: 'flow-msg-time-answer-autumn', defaultMessage: 'September-November'}),
        intl.formatMessage({id: 'flow-msg-time-answer-winter', defaultMessage: 'December-February'}),
      ],
      path: 'handleTimeAnswer',
      chatDisabled: true,
    },
    handleTimeAnswer: {
      function: () => {
        startAnswersFlowTimer();
      },
      transition: {duration: 0},
      path: async (params) => {
        await formik.setFieldValue('timeOfYear', params.userInput);
        return 'regionsQuestion';
      },
      chatDisabled: true,
    },
    regionsQuestion: {
      message: () => intl.formatMessage({
        id: 'flow-msg-regions-question',
        defaultMessage: 'Which region is the vacation planned for?'
      }),
      options: [
        intl.formatMessage({id: 'flow-msg-regions-answer-western-galilee', defaultMessage: 'Western Galilee'}),
        intl.formatMessage({id: 'flow-msg-regions-answer-upper-galilee', defaultMessage: 'Upper Galilee'}),
        intl.formatMessage({id: 'flow-msg-regions-answer-southern-arava', defaultMessage: 'Southern Arava'}),
      ],
      path: 'handleRegionsAnswer',
      chatDisabled: true,
    },
    handleRegionsAnswer: {
      transition: {duration: 0},
      path: async (params) => {
        await formik.setFieldValue('region', params.userInput);
        return 'kidsQuestion';
      },
      chatDisabled: true,
    },
    kidsQuestion: {
      message: () => intl.formatMessage({id: 'flow-msg-kids-question', defaultMessage: 'What time of year?'}),
      options: [
        intl.formatMessage({id: 'flow-msg-kids-answer-yes', defaultMessage: 'Yes'}),
        intl.formatMessage({id: 'flow-msg-kids-answer-no', defaultMessage: 'No'}),
      ],
      path: 'handleKidsAnswer',
      chatDisabled: true,
    },
    handleKidsAnswer: {
      transition: {duration: 0},
      path: async (params) => {
        await formik.setFieldValue('kids', params.userInput === intl.formatMessage({
          id: 'flow-msg-kids-answer-yes',
          defaultMessage: 'Yes'
        }));
        return 'babiesQuestion';
      },
      chatDisabled: true,
    },
    babiesQuestion: {
      message: () => intl.formatMessage({id: 'flow-msg-babies-question', defaultMessage: 'What time of year?'}),
      options: [
        intl.formatMessage({id: 'flow-msg-babies-answer-yes', defaultMessage: 'Yes'}),
        intl.formatMessage({id: 'flow-msg-babies-answer-no', defaultMessage: 'No'}),
      ],
      path: 'handleBabiesAnswer',
      chatDisabled: true,
    },
    handleBabiesAnswer: {
      transition: {duration: 0},
      path: async (params) => {
        await formik.setFieldValue('babies', params.userInput === intl.formatMessage({
          id: 'flow-msg-babies-answer-yes',
          defaultMessage: 'Yes'
        }));
        return 'petsQuestion';
      },
      chatDisabled: true,
    },
    petsQuestion: {
      message: () => intl.formatMessage({id: 'flow-msg-pets-question', defaultMessage: 'Do you have a dog?'}),
      options: [
        intl.formatMessage({id: 'flow-msg-pets-answer-yes', defaultMessage: 'Yes'}),
        intl.formatMessage({id: 'flow-msg-pets-answer-no', defaultMessage: 'No'}),
      ],
      path: async (params) => {
        const isYesSelected = params.userInput === intl.formatMessage({
          id: 'flow-msg-pets-answer-yes',
          defaultMessage: 'Yes'
        });
        if (isYesSelected) {
          animations.dog.show();
        }
        await formik.setFieldValue('dogs', isYesSelected);
        return 'handlePetsAnswer';
      },
      chatDisabled: true,
    },
    handlePetsAnswer: {
      transition: {duration: formik.values.dogs ? 1000 : 0},
      path: 'activitiesQuestion',
      chatDisabled: true,
    },
    activitiesQuestion: {
      message: () => intl.formatMessage({
        id: 'flow-msg-activities-question',
        defaultMessage: 'What would you be interested in including in the vacation or trip?'
      }),
      checkboxes: {
        items: [
          intl.formatMessage({id: 'flow-msg-activities-answer-hiking', defaultMessage: 'Hiking trails'}),
          intl.formatMessage({id: 'flow-msg-activities-answer-extreme', defaultMessage: 'Extreme'}),
          intl.formatMessage({id: 'flow-msg-activities-answer-children', defaultMessage: 'Attractions for children'}),
          intl.formatMessage({
            id: 'flow-msg-activities-answer-restaurants',
            defaultMessage: 'Coffee carts and restaurants'
          }),
          intl.formatMessage({id: 'flow-msg-activities-answer-wineries', defaultMessage: 'Wineries and breweries'}),
          intl.formatMessage({id: 'flow-msg-activities-answer-museums', defaultMessage: 'Museums and visitor centers'}),
          intl.formatMessage({id: 'flow-msg-activities-answer-workshops', defaultMessage: 'Workshops'}),
        ],
        min: 1
      },
      path: async (params) => {
        const activities = params.userInput.split(',').map(item => item.trim());
        await formik.setFieldValue('activities', activities, false);
        return 'goodChoice';
      },
      chatDisabled: true,
    },
    goodChoice: {
      message: () => intl.formatMessage({
        id: 'flow-msg-great-choice',
        defaultMessage: 'Great choice! I have recommendations just for you!'
      }),
      transition: {duration: 0},
      path: 'handleActivitiesAnswer',
    },
    handleActivitiesAnswer: {
      transition: {duration: 0},
      path: () => {
        if (formik.values.activities.includes(intl.formatMessage({
          id: 'flow-msg-activities-answer-hiking',
          defaultMessage: 'Hiking trails'
        }))) {
          return 'trailsQuestion';
        } else if (formik.values.activities.includes(intl.formatMessage({
          id: 'flow-msg-activities-answer-restaurants',
          defaultMessage: 'Coffee carts and restaurants'
        }))) {
          return 'mealQuestion';
        } else {
          return 'saveAnswers';
        }
      },
      chatDisabled: true,
    },
    trailsQuestion: {
      message: () => intl.formatMessage({
        id: 'flow-msg-trails-question',
        defaultMessage: 'What is the desired type of trail?'
      }),
      options: [
        intl.formatMessage({id: 'flow-msg-trails-answer-accessible', defaultMessage: 'Accessible'}),
        intl.formatMessage({id: 'flow-msg-trails-answer-easy', defaultMessage: 'Easy'}),
        intl.formatMessage({id: 'flow-msg-trails-answer-moderate', defaultMessage: 'Moderate'}),
        intl.formatMessage({id: 'flow-msg-trails-answer-challenging', defaultMessage: 'Challenging'}),
      ],
      path: 'handleTrailsAnswer',
      chatDisabled: true,
    },
    handleTrailsAnswer: {
      transition: {duration: 0},
      path: async (params) => {
        await formik.setFieldValue('trailsOptions', params.userInput);
        if (formik.values.activities.includes(intl.formatMessage({
          id: 'flow-msg-activities-answer-restaurants',
          defaultMessage: 'Coffee carts and restaurants'
        }))) {
          return 'mealQuestion';
        } else {
          return 'saveAnswers';
        }
      },
      chatDisabled: true,
    },
    mealQuestion: {
      message: () => intl.formatMessage({
        id: 'flow-msg-meal-question',
        defaultMessage: 'Should I recommend vegan/vegetarian/kosher options?'
      }),
      options: [
        intl.formatMessage({id: 'flow-msg-meal-answer-vegan', defaultMessage: 'Vegan'}),
        intl.formatMessage({id: 'flow-msg-meal-answer-vegetarian', defaultMessage: 'Vegetarian'}),
        intl.formatMessage({id: 'flow-msg-meal-answer-kosher', defaultMessage: 'Kosher'}),
        intl.formatMessage({id: 'flow-msg-meal-answer-nothing', defaultMessage: 'Nothing'}),
      ],
      path: 'handleMealAnswer',
      chatDisabled: true,
    },
    handleMealAnswer: {
      transition: {duration: 0},
      path: async (params) => {
        await formik.setFieldValue('mealOptions', params.userInput);
        return 'saveAnswers';
      },
      chatDisabled: true,
    },
    saveAnswers: {
      transition: {duration: 0},
      function: async () => {
        stopAnswersFlowTimer();
        await saveAnswers(formik.values);
        await updateContactAndUser(formik.values);
      },
      path: 'initialConnectAIAgent',
      chatDisabled: false,
    },
    initialConnectAIAgent: {
      message: async (params) => {
        startAssistantFlowTimer();
        await callAiAgent(params, getQuestionnaireData());
        enableSendEmailButton();
      },
      renderMarkdown: ['BOT', 'USER'],
      path: () => {
        if (error) {
          return 'end';
        }
        return 'connectAIAgent';
      },
      chatDisabled: false,
    } as MarkdownRendererBlock,
    connectAIAgent: {
      message: async (params) => {
        handleUserInputAnimations(params.userInput);
        await callAiAgent(params);
      },
      renderMarkdown: ['BOT', 'USER'],
      path: () => {
        if (error) {
          return 'end';
        }
        return 'connectAIAgent';
      },
      chatDisabled: false,
    } as MarkdownRendererBlock,
    end: {
      function: () => {
        clearFlowTimers();
      },
      chatDisabled: true
    },
    ...timeoutMessagesFlow
  };

  return (
    <NewUserChatContext.Provider
      value={{
        id: 'ai-chat',
        flow
      }}
    >
      {children}
    </NewUserChatContext.Provider>
  );
};
